import { t } from "@lingui/macro";
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { ReactNode, useCallback, useMemo } from "react";
import useAlert from "../hooks/useAlert";
import useAuth from "../hooks/useAuth";
import useConfig from "../hooks/useConfig";

export default function QueryProvider({ children }: { children: ReactNode }) {
  const { locale } = useConfig();
  const { onLogout } = useAuth();
  const alert = useAlert();
  const handleQueryError = useCallback(
    (error: unknown, query?: any) => {
      // Check if the query has an ignoreStatusCodes option in meta
      const ignoreStatusCodes = query?.meta?.ignoreStatusCodes || [];
      let msg = t`Unexpected Error`;
      if (error instanceof AxiosError) {
        const { response, request } = error;
        const status = request?.status ?? response?.status;
        // Ignore status codes that are in the ignoreStatusCodes array
        if (ignoreStatusCodes.includes(status)) return;

        if (response?.data.message) msg = response.data.message;
        if (status === 401) {
          onLogout();
          msg = t`You need to login to continue`;
        } else if (status === 0) msg = t`Can not reach server.`;
      }
      else if (error instanceof Error) {
        msg = error.message;
      }
      alert({ title: t`Error`, description: msg });
    },
    [onLogout, alert, locale] // eslint-disable-line
  );

  const handleMutationError = useCallback(
    (error: unknown, _variables?: any, _context?: any, mutation?: any) => {
      // Check if the mutation has an ignoreStatusCodes option in meta
      const ignoreStatusCodes = mutation?.options?.meta?.ignoreStatusCodes || [];
      let status: number | undefined = undefined;
      if (error instanceof AxiosError) {
        const { response, request } = error;
        status = request?.status ?? response?.status;
        // Ignore status codes that are in the ignoreStatusCodes array
        if (ignoreStatusCodes.includes(status)) return;
        const errors = error.response?.data?.errors;
        if (typeof errors !== "object") return handleQueryError(error);

        Object.keys(errors).forEach((key: any) => {
          const error = Array.isArray(errors[key]) ? errors[key].join(", ") : errors[key];
          alert({ title: key, description: error });
        });
      } else handleQueryError(error)
    },
    [handleQueryError, alert]
  );

  const queryClient = useMemo(() => {
    return new QueryClient({
      defaultOptions: {
        queries: {
          retry: false,
        },
      },
      queryCache: new QueryCache({ onError: handleQueryError as any }),
      mutationCache: new MutationCache({ onError: handleMutationError as any }),
    });
  }, []); // eslint-disable-line

  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
};
